import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Check, Infinity as InfinityIcon, Coins, Layers, Mail } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const ICONS = { pack50: Coins, pack100: Layers, month: InfinityIcon };

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [ent, setEnt] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadEnt = async () => {
    if (!user) return setEnt(null);
    try {
      const { data } = await api.get("/billing/me");
      setEnt(data);
    } catch {
      setEnt(null);
    }
  };

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);
  useEffect(() => { loadEnt(); }, [user]);

  const buy = async (planId) => {
    if (!user) return navigate("/login", { state: { from: "/pricing" } });
    setBusy(planId);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("checkout script");
      const { data } = await api.post("/billing/order", { plan_id: planId });

      const rz = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: data.currency,
        name: "LedgerCheck",
        description: data.plan.label,
        prefill: { name: data.name, email: data.email },
        theme: { color: "#0f172a" },
        handler: async (res) => {
          try {
            const { data: fresh } = await api.post("/billing/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            setEnt(fresh);
            toast.success("Payment successful — your account is topped up");
            navigate("/analyze");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Payment received but activation failed. Contact support.");
          }
        },
        modal: { ondismiss: () => toast.message("Payment cancelled") },
      });
      rz.on("payment.failed", () => toast.error("Payment failed. Nothing was charged."));
      rz.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the payment. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grain min-h-screen">
      <Nav />
      <main className="mx-auto max-w-[1100px] px-6 py-14 lg:px-12">
        <p className="overline text-slate-500">Pricing</p>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Pay for what you review</h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">
          Every account gets 50 free reviews. After that, top up with a pack or go unlimited for a month.
        </p>

        {user ? (
          <div className="mt-8 inline-flex items-center gap-6 border border-stone-200 bg-white px-6 py-4" data-testid="balance-box">
            {ent ? (
              ent.unlimited ? (
                <span className="font-mono-data text-sm">Unlimited until {new Date(ent.pass_until).toLocaleDateString("en-IN")}</span>
              ) : (
                <>
                  <span className="font-mono-data text-sm" data-testid="balance-reviews">{ent.reviews_left} reviews left</span>
                  <span className="font-mono-data text-xs text-slate-400">
                    {ent.free_left} free · {ent.credits} paid
                  </span>
                </>
              )
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            )}
          </div>
        ) : (
          <p className="mt-8 font-mono-data text-xs text-slate-500">
            You are using the free guest allowance (50 reviews).{" "}
            <Link to="/login" className="underline hover:text-[#0f172a]">Create a free account</Link> for 100 free reviews.
          </p>
        )}

        <div className="mt-14 grid gap-8 md:grid-cols-2">
          {plans.map((p) => {
            const Icon = ICONS[p.id] || Coins;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className="group flex flex-col border border-stone-200 bg-white p-10 transition-transform duration-150 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0f172a]">
                <Icon className="h-5 w-5 text-[#0f172a]" />
                <h2 className="font-display mt-6 text-2xl font-bold tracking-tight md:text-lg">{p.label}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.blurb}</p>
                <p className="font-mono-data mt-8 text-3xl font-semibold">₹{(p.amount / 100).toLocaleString("en-IN")}</p>
                {p.type === "credits" && (
                  <p className="font-mono-data mt-2 text-[11px] text-slate-400">₹{(p.amount / 100 / p.credits).toFixed(2)} per review</p>
                )}
                <ul className="mt-6 space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-700" /> {p.type === "credits" ? `${p.credits} entry reviews` : "Unlimited reviews for 30 days"}</li>
                  <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-700" /> Ind AS &amp; AS, GST and Companies Act notes</li>
                  <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-700" /> Invoice PDF and day-book uploads</li>
                </ul>
                <button onClick={() => buy(p.id)} disabled={busy === p.id} data-testid={`buy-${p.id}`}
                  className="mt-10 flex items-center justify-center gap-2 bg-[#0f172a] px-6 py-4 text-sm font-medium text-white transition-colors duration-150 hover:bg-stone-800 disabled:opacity-60">
                  {busy === p.id ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening checkout…</> : user ? "Buy now" : "Sign in to buy"}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-12 font-mono-data text-[11px] leading-relaxed text-slate-400">
          Payments are processed by Razorpay. Compare Ind AS vs AS costs 2 reviews; a day-book upload costs 1 review per
          entry that gets an AI review. Comparison, day-book uploads and saved history need a free account.
        </p>

        <div className="mt-10 flex justify-end">
          <a href="mailto:kamalaggarwalscc@gmail.com" data-testid="pricing-contact-tab"
            className="inline-flex items-center gap-2 border border-stone-300 bg-white px-5 py-3 font-mono-data text-[11px] text-slate-500 transition-colors duration-150 hover:border-[#0f172a] hover:text-[#0f172a]">
            <Mail className="h-3.5 w-3.5" />
            Reach out to team @kamalaggarwalscc@gmail.com
          </a>
        </div>
      </main>
    </div>
  );
}
